"use client"

import {Container} from '@/components/container'
import {FrequencySelector} from "@/components/frequency-selector";
import type {Product} from "@/common/types";
import React, {useEffect, useState} from "react";
import PricingDoodle from "@/components/pricing-doodle";
import PricingPlan from "@/components/pricing-plan";
import logger from "@/logger";

type DiscountData = {
  "code": string,
  "discount": number,
  "expiresAt"?: string,
  "prices"?: string[],
  "products"?: string[]
}

export function Pricing({products}: Readonly<{ products: Product[] }>) {
  const [modifiedProducts, setModifiedProducts] = useState(products);
  const [frequency, setFrequency] = useState('year')

  useEffect(() => {
    const params = new URL(document.location.toString()).searchParams;
    const refCookie = document.cookie.split("; ").find(x => x.startsWith("ref="))?.split("=")[1];
    const refCode = params.get("ref") ?? refCookie;
    logger.debug('Ref code detected:', refCode);

    if (refCode) {
      applyCoupon(refCode, products).then((p) => setModifiedProducts(p));
    }
  }, [products]);

  const regions: {
    [key: string]: any
  } = {
    "eu-1": "Europe",
    "us-1": "United States"
  }

  return (
    <section id="pricing" aria-label="Pricing" className="bg-slate-900 py-20 sm:py-32">
      <Container>
        <div className="md:text-center">
          <h1 className="font-display text-3xl tracking-tight text-white sm:text-4xl">
            Get your own{' '}
            <span className="relative whitespace-nowrap">
              <PricingDoodle/>
              <span className="relative">personal server,</span>
            </span>{' '}
            for a fraction of the cost.
          </h1>
          <p className="mt-4 text-lg text-slate-400">
            Your own private server, with a dedicated IP address, your own emails, large file transfer system and VPN is
            just a click away.
          </p>
        </div>
        <FrequencySelector frequency={frequency} setFrequency={setFrequency}/>
        <div
          className="-mx-4 mt-16 grid max-w-2xl grid-cols-1 gap-y-10 sm:mx-auto lg:-mx-8 lg:max-w-none lg:grid-cols-3 xl:mx-0 xl:gap-x-8">
          {modifiedProducts.length > 0 && modifiedProducts.filter((product: Product) => product.server.featured).map((product: Product, i) => <PricingPlan
            index={i}
            key={product.id}
            name={product.name}
            frequency={frequency}
            prices={product.prices}
            description={product.server.description}
            free_domain={product.server.free_domain}
            href={`/checkout/select-region?product=${product.id}&price=${(product.prices.find((p) => p.recurring_interval === frequency)?.id)}`}
            featured={product.server.showcase}
            features={[
              '14 day money back guarantee',
              'Available in ' + product.server.regions.map((region) => regions[region]).join(', '),
              product.server.user_limit > 0 ? product.server.user_limit + ' users' : 'Unlimited users!',
              '10gbit network',
              product.server.cpu + ' CPU cores',
              product.server.memory + 'GB RAM',
              product.server.disk + 'GB disk space ¹',
              '1TB monthly transfer ²',
              'Email server compatible with every email client',
              'Large file transfer system',
              'Your own VPN server with iOS, Android, macOS support',
            ]}
          />)}
        </div>
      </Container>
    </section>
  )
}

async function applyCoupon(refCode: string, products: Product[]): Promise<Product[]> {
  return await fetch(`/api/public/coupon/${refCode}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to apply discount");
      }
      return response.json();
    })
    .then((data: DiscountData) => {
      return products.map((product) => {
        const newProduct = {...product};
        const isValidForProduct = !data.products || data.products?.includes(product.id);

        if (isValidForProduct) {
          if (data.prices) {
            for (let i = 0; i < newProduct.prices.length; i++) {
              if (data.prices.includes(newProduct.prices[i].id)) {
                const price = newProduct.prices[i];

                if (price.recurring_interval === "year") {
                  price.pre_discount_unit_amount = price.unit_amount;
                  price.unit_amount = price.unit_amount - (price.unit_amount * data.discount / 100);
                  newProduct.prices[i] = price;
                }
              }
            }
          } else {
            newProduct.prices = newProduct.prices.map((price) => {
              if (price.recurring_interval === "year") {
                price.pre_discount_unit_amount = price.unit_amount;
                price.unit_amount = price.unit_amount - (price.unit_amount * data.discount / 100);
              }
              return price;
            });
          }
        }

        return newProduct;
      })
    })
    .catch((error) => {
      logger.warn({error, message: "Failed to apply discount"});
      return products;
    });
}
